import React, { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { newGuid, ticksToDate } from '../../../Utils';
import SpinnerNew from '../SpinnerNew/SpinnerNew';
import SearchBoxNew from '../SearchBoxNew/SearchBoxNew';
import { FilterTag } from '../../../App.enums';
import { Tag } from '../../../generated/graphql';
import { Check, Loader2, Minus, PackageOpen } from 'lucide-react';
import TagBadge from '../TagBadge/TagBadge';
import DataTableHeaderCell from './DataTableHeaderCell';
import { getNestedValue, nextSort, sortRows, TableSort, TableSortValue } from './DataTableSorting';

export type DataTableColumn = {
    id: string;
    name: string;
    width?: string;
    render?: (row: any) => ReactNode;
    /** Enables in-memory sorting for a complete dataset. */
    sortable?: boolean;
    /** GraphQL sort-input field. Its presence enables server sorting. */
    sortField?: string;
    /** Value used by in-memory sorting; defaults to the column's nested ID path. */
    sortValue?: (row: any) => TableSortValue;
};

type DataTableBaseProps = {
    isLoading: boolean;
    data: any[] | undefined | null;
    columns?: DataTableColumn[];
    showName?: boolean;
    showDescription?: boolean;
    showTags?: boolean;
    showCreatedBy?: boolean;
    showCreatedDate?: boolean;
    link?: string;
    onRowClick?: (row: any) => void;
    searchable?: boolean;
    filterable?: boolean;
    selectable?: boolean;
    showCheckboxes?: boolean;
    controlledSelectedIds?: string[];
    onSearchChanged?: (value: string) => void;
    onFilterChanged?: (tags: FilterTag[]) => void;
    toolbarControls?: ReactNode;
    onSelected?: (ids: string[]) => void;
    fetchMore?: () => void;
    style?: CSSProperties;
    children?: ReactNode;
    emptyTitle?: string;
    emptyDescription?: string;
    /** Stable table name used to remember user-resized column widths. */
    persistenceKey?: string;
};

type DataTableSortingProps =
    | {
        sortingMode: 'none';
        sort?: never;
        defaultSort?: never;
        onSortChange?: never;
    }
    | {
        sortingMode: 'client';
        sort?: never;
        defaultSort?: TableSort | null;
        onSortChange?: never;
    }
    | {
        sortingMode: 'server';
        sort: TableSort | null;
        defaultSort?: never;
        onSortChange: (sort: TableSort | null) => void;
    };

export type DataTableNewProps = DataTableBaseProps & DataTableSortingProps;

const COLUMN_WIDTH_STORAGE_PREFIX = 'cms:data-table:column-widths:v1';
const MIN_COLUMN_WIDTH = 60;

const readStoredColumnWidths = (storageKey: string): Record<string, number> => {
    if (typeof window === 'undefined') return {};

    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}');
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

        return Object.fromEntries(
            Object.entries(parsed).filter((entry): entry is [string, number] =>
                typeof entry[1] === 'number'
                && Number.isFinite(entry[1])
                && entry[1] >= MIN_COLUMN_WIDTH,
            ),
        );
    } catch {
        return {};
    }
};

const storeColumnWidths = (storageKey: string, widths: Record<string, number>) => {
    if (typeof window === 'undefined') return;

    try {
        if (Object.keys(widths).length === 0) {
            window.localStorage.removeItem(storageKey);
        } else {
            window.localStorage.setItem(storageKey, JSON.stringify(widths));
        }
    } catch {
        // Storage may be unavailable; resizing still works for this session.
    }
};

const DataTableNew = (props: DataTableNewProps) => {
    const {
        isLoading, data, columns, showName = true, showDescription, showTags, showCreatedBy, showCreatedDate,
        link, onRowClick, searchable = true, filterable, selectable = true, showCheckboxes = false,
        controlledSelectedIds,
        onSearchChanged, onFilterChanged, toolbarControls, onSelected, fetchMore,
        style, emptyTitle, emptyDescription, persistenceKey
    } = props;

    const columnSignature = [
        showName ? 'name' : null,
        ...(columns?.map(column => column.id) ?? []),
        showDescription ? 'description' : null,
        showTags ? 'tags' : null,
        showCreatedBy ? 'createdBy' : null,
        showCreatedDate ? 'createdTime' : null,
    ].filter(Boolean).join('|');
    const routeScope = typeof window === 'undefined' ? 'unknown-route' : window.location.pathname;
    const columnWidthsStorageKey = `${COLUMN_WIDTH_STORAGE_PREFIX}:${persistenceKey || `${routeScope}:${columnSignature}`}`;

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Sync internal state with controlled prop when it changes externally
    useEffect(() => {
        if (controlledSelectedIds !== undefined) {
            setSelectedIds(new Set(controlledSelectedIds));
        }
    }, [controlledSelectedIds]);

    // Column resize
    const loadedColumnWidthsKeyRef = useRef(columnWidthsStorageKey);
    const [colWidths, setColWidths] = useState<Record<string, number>>(
        () => readStoredColumnWidths(columnWidthsStorageKey),
    );
    const thRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());
    const resizeDragRef = useRef<{ colId: string; startX: number; startWidth: number } | null>(null);

    const startResize = useCallback((e: React.MouseEvent, colId: string) => {
        e.preventDefault();
        e.stopPropagation();
        const th = thRefs.current.get(colId);
        if (!th) return;
        const startWidth = th.getBoundingClientRect().width;
        resizeDragRef.current = { colId, startX: e.clientX, startWidth };
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        const onMouseMove = (ev: MouseEvent) => {
            if (!resizeDragRef.current) return;
            const delta = ev.clientX - resizeDragRef.current.startX;
            const newWidth = Math.max(MIN_COLUMN_WIDTH, resizeDragRef.current.startWidth + delta);
            setColWidths(prev => ({ ...prev, [resizeDragRef.current!.colId]: newWidth }));
        };
        const onMouseUp = () => {
            resizeDragRef.current = null;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, []);

    // Persist only presentation state. The key check prevents widths from one table
    // being briefly written into another if a mounted table changes context.
    useEffect(() => {
        if (loadedColumnWidthsKeyRef.current === columnWidthsStorageKey) {
            storeColumnWidths(columnWidthsStorageKey, colWidths);
        }
    }, [colWidths, columnWidthsStorageKey]);

    useEffect(() => {
        if (loadedColumnWidthsKeyRef.current !== columnWidthsStorageKey) {
            loadedColumnWidthsKeyRef.current = columnWidthsStorageKey;
            setColWidths(readStoredColumnWidths(columnWidthsStorageKey));
        }
    }, [columnWidthsStorageKey]);

    const resetColumnWidth = useCallback((columnId: string) => {
        setColWidths(currentWidths => {
            const nextWidths = { ...currentWidths };
            delete nextWidths[columnId];
            return nextWidths;
        });
    }, []);
    const [clientSort, setClientSort] = useState<TableSort | null>(
        props.sortingMode === 'client' ? props.defaultSort ?? null : null,
    );
    const [localFilterTags, setLocalFilterTags] = useState<FilterTag[]>([]);
    const [clientSearchTerms, setClientSearchTerms] = useState<string[]>([]);
    const lastClickedIndex = useRef<number | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const isSelectingAllRef = useRef(false);
    const prevDataLengthRef = useRef<number>(0);
    // Always-current refs — updated every render so effects/handlers never see stale values
    const dataRef = useRef<typeof data>(undefined);
    dataRef.current = data;
    const localFilterTagsRef = useRef<FilterTag[]>([]);
    localFilterTagsRef.current = localFilterTags;
    const fetchMoreRef = useRef(fetchMore);
    fetchMoreRef.current = fetchMore;
    const onSelectedRef = useRef(onSelected);
    onSelectedRef.current = onSelected;

    const handleFilterChanged = (tags: FilterTag[]) => {
        setLocalFilterTags(tags);
        // Only pass non-inverse tags to the parent (server-side query)
        onFilterChanged && onFilterChanged(tags.filter(t => !t.inverse));
    };

    // Client-side post-filter: exclude items whose tagIds include any inverse filter tag ID
    const inverseTagIds = localFilterTags.filter(t => t.inverse).map(t => t.id || '');
    const tagFilteredData = inverseTagIds.length > 0
        ? data?.filter(item => !inverseTagIds.some(id => (item.tagIds as string[] | undefined)?.includes(id)))
        : data;

    // Client-side search term filtering (from chip/multi-term search)
    // Match against name + description only (same approach as the classifications dialog)
    const filteredData = clientSearchTerms.length > 0
        ? tagFilteredData?.filter(item => {
            if (!item) return false;
            const name = item.name || '';
            const desc = item.description || '';
            const target = `${name} ${desc}`.toLowerCase();
            return clientSearchTerms.every(term => target.includes(term.toLowerCase()));
        })
        : tagFilteredData;

    // Re-applies all active client-side filters against the most current data ref.
    // Called only when we know all pages are loaded — always computes from scratch
    // using the latest refs so there's no risk of stale closure values.
    const finalizeSelectAll = () => {
        const currentData = dataRef.current || [];
        const currentInverseTagIds = localFilterTagsRef.current
            .filter(t => t.inverse)
            .map(t => t.id || '');
        const finalData = currentInverseTagIds.length > 0
            ? currentData.filter(item =>
                !currentInverseTagIds.some(id => (item.tagIds as string[] | undefined)?.includes(id))
              )
            : currentData;
        const allIds = finalData.map((r: any) => r.id).filter(Boolean) as string[];
        setSelectedIds(new Set(allIds));
        onSelectedRef.current?.(allIds);
        isSelectingAllRef.current = false;
        setIsSelectingAll(false);
    };

    // Call fetchMore and return true if a fetch was actually started,
    // false if the parent's guard determined there are no more pages.
    const tryFetchMore = (): boolean => {
        const fm = fetchMoreRef.current;
        if (!fm) return false;
        // Parents guard with `hasNextPage && fetchMore(...)` which returns `false`
        // when there is no next page. A real fetch returns a Promise (truthy).
        const result = (fm as () => any)();
        return !!result && result !== false;
    };

    const handleSelectAllPages = () => {
        if (!fetchMore) {
            // No pagination — select current filtered data immediately
            const allIds = (filteredData || []).map(r => r.id).filter(Boolean);
            updateSelection(new Set(allIds));
            return;
        }
        prevDataLengthRef.current = data?.length ?? 0;
        isSelectingAllRef.current = true;
        setIsSelectingAll(true);
        // If fetchMore is already a no-op (all pages loaded), finalize right away
        if (!tryFetchMore()) {
            finalizeSelectAll();
        }
    };

    // Watch raw data length only — fires when a new page arrives.
    // Uses isSelectingAllRef (not state) to avoid the race where setIsSelectingAll(true)
    // triggers this effect before `data` has actually grown.
    useEffect(() => {
        if (!isSelectingAllRef.current) return;
        const currentLength = dataRef.current?.length ?? 0;
        if (currentLength > prevDataLengthRef.current) {
            prevDataLengthRef.current = currentLength;
            // Try to load the next page; if none left, finalize
            if (!tryFetchMore()) {
                finalizeSelectAll();
            }
        }
        // currentLength === prev means a non-data re-render fired the effect — ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Auto-load more pages whenever the scroll container isn't full enough to scroll.
    // This covers both the inverse-filter case (sparse results) and the initial load
    // case where the first page doesn't fill the viewport.
    useEffect(() => {
        if (!fetchMore) return;
        const container = scrollContainerRef.current;
        if (container && container.scrollHeight <= container.clientHeight + 10) {
            fetchMore();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredData]);

    const updateSelection = (next: Set<string>) => {
        setSelectedIds(next);
        onSelected && onSelected(Array.from(next));
    };

    const toggleSelection = (id: string, index: number, shiftKey: boolean, visibleRows: any[]) => {
        const next = new Set(selectedIds);

        if (shiftKey && lastClickedIndex.current !== null) {
            const start = Math.min(lastClickedIndex.current, index);
            const end = Math.max(lastClickedIndex.current, index);
            for (let i = start; i <= end; i++) {
                const rowId = visibleRows[i]?.id;
                if (rowId) next.add(rowId);
            }
        } else {
            if (next.has(id)) next.delete(id);
            else next.add(id);
        }

        lastClickedIndex.current = index;
        updateSelection(next);
    };

    const toggleSelectAll = () => {
        if (!filteredData) return;
        const allIds = filteredData.map(r => r.id).filter(Boolean);
        const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
        if (allSelected) {
            updateSelection(new Set());
        } else {
            updateSelection(new Set(allIds));
        }
    };

    const activeSort = props.sortingMode === 'server' ? props.sort : clientSort;

    const handleSort = (field: string) => {
        if (props.sortingMode === 'server') {
            // A paged query must always have a deterministic order. Server headers
            // therefore toggle between ascending and descending rather than
            // exposing a misleading "unsorted" state backed by a hidden fallback.
            props.onSortChange({
                field,
                direction: activeSort?.field === field && activeSort.direction === 'asc' ? 'desc' : 'asc',
            });
        }
        if (props.sortingMode === 'client') setClientSort(nextSort(activeSort, field));
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.target as HTMLDivElement;
        if (target.scrollTop + 10 >= target.scrollHeight - target.clientHeight) {
            fetchMore && fetchMore();
        }
    };

    const allColumns = useMemo<DataTableColumn[]>(() => {
        const builtColumns: DataTableColumn[] = [];

        if (showName) {
            builtColumns.push({
                id: 'name',
                name: 'Name',
                sortable: true,
                sortField: 'name',
                render: link ? (row) => (
                    <Link
                        to={`${link}/${row.id}`}
                        className="font-medium hover:underline transition-colors"
                        style={{ color: 'var(--accent-text)' }}
                    >
                        {row.name}
                    </Link>
                ) : onRowClick ? (row) => (
                    <button
                        onClick={(e) => { e.stopPropagation(); onRowClick(row); }}
                        className="font-medium hover:underline transition-colors text-left"
                        style={{ color: 'var(--accent-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >
                        {row.name}
                    </button>
                ) : undefined,
            });
        }

        if (columns) {
            builtColumns.push(...columns);
        }

        if (showDescription) {
            builtColumns.push({ id: 'description', name: 'Description', sortable: true, sortField: 'description' });
        }

        if (showTags) {
            builtColumns.push({
                id: 'tags',
                name: 'Tags',
                width: '250px',
                sortable: true,
                sortValue: (row) => row.tags?.filter(Boolean).map((tag: Tag) => `${tag.type ?? ''}: ${tag.name ?? ''}`).join(', ') ?? '',
                render: (row) => (
                    <div className="flex flex-wrap gap-1">
                        {row.tags?.filter((t: any) => t).map((t: Tag) => (
                            <TagBadge key={t.id || newGuid()} tag={t} />
                        ))}
                    </div>
                ),
            });
        }

        if (showCreatedBy) {
            builtColumns.push({
                id: 'createdBy',
                name: 'Created By',
                width: '140px',
                sortable: true,
                sortValue: (row) => row.createdBy?.name ?? '',
                render: (row) => <span>{row.createdBy?.name}</span>,
            });
        }

        if (showCreatedDate) {
            builtColumns.push({
                id: 'createdTime',
                name: 'Created',
                width: '130px',
                sortable: true,
                sortField: 'createdTime',
                sortValue: (row) => row.createdTime ?? null,
                render: (row) => <span>{row.createdTime ? ticksToDate(row.createdTime) : ''}</span>,
            });
        }

        return builtColumns;
    }, [columns, link, onRowClick, showCreatedBy, showCreatedDate, showDescription, showName, showTags]);

    const columnSortField = (column: DataTableColumn): string | undefined => {
        if (props.sortingMode === 'server') return column.sortField;
        if (props.sortingMode === 'client' && column.sortable) return column.id;
        return undefined;
    };

    const displayedData = useMemo(() => {
        const rows = filteredData ?? [];
        if (props.sortingMode !== 'client') return rows;
        return sortRows(rows, clientSort, (row, field) => {
            const column = allColumns.find(item => item.id === field);
            const value = column?.sortValue ? column.sortValue(row) : getNestedValue(row, field);
            return value instanceof Date || ['string', 'number', 'boolean'].includes(typeof value)
                ? value as TableSortValue
                : value === null || value === undefined ? value : String(value);
        });
    }, [filteredData, clientSort, props.sortingMode, allColumns]);

    useEffect(() => {
        if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
    }, [activeSort?.field, activeSort?.direction]);

    return (
        <div className="flex flex-col h-full" style={style}>
            {/* Search Bar */}
            {searchable && onSearchChanged && (
                <SearchBoxNew
                    onSearchChanged={onSearchChanged}
                    onFilterChanged={filterable ? handleFilterChanged : undefined}
                    onClientSearchTermsChanged={setClientSearchTerms}
                    toolbarControls={toolbarControls}
                />
            )}

            {/* Selection action bar */}
            {selectable && (selectedIds.size > 0 || isSelectingAll) && (
                <div
                    className="flex items-center gap-3 px-4 py-2 text-xs font-medium"
                    style={{
                        borderBottom: '1px solid var(--border-default)',
                        backgroundColor: 'var(--accent-soft)',
                        color: 'var(--accent-text)',
                    }}
                >
                    <span>
                        {isSelectingAll
                            ? `Loading pages… (${data?.length ?? 0} loaded)`
                            : `${selectedIds.size} selected`}
                    </span>
                    <button
                        onClick={handleSelectAllPages}
                        disabled={isSelectingAll}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors"
                        style={{
                            backgroundColor: 'var(--accent)',
                            color: 'white',
                            border: 'none',
                            cursor: isSelectingAll ? 'not-allowed' : 'pointer',
                            opacity: isSelectingAll ? 0.7 : 1,
                        }}
                    >
                        {isSelectingAll
                            ? <><Loader2 size={11} className="animate-spin" /> Loading all pages…</>
                            : `Select all pages${filteredData?.length !== data?.length ? ` (${filteredData?.length} filtered / ${data?.length} loaded)` : data?.length ? ` (${data.length})` : ''}`}
                    </button>
                    {selectedIds.size > 0 && !isSelectingAll && (
                        <button
                            onClick={() => updateSelection(new Set())}
                            className="px-2.5 py-1 rounded-md transition-colors"
                            style={{
                                background: 'none',
                                border: '1px solid var(--accent)',
                                color: 'var(--accent-text)',
                                cursor: 'pointer',
                            }}
                        >
                            Clear selection
                        </button>
                    )}
                </div>
            )}

            {/* Table */}
            <div ref={scrollContainerRef} className="flex-1 min-h-0 overflow-auto" onScroll={handleScroll}>
                {isLoading ? (
                    <SpinnerNew size="large" label="Loading data..." />
                ) : displayedData.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">
                            <PackageOpen size={24} />
                        </div>
                        <div className="text-center">
                            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                {emptyTitle || 'No items found'}
                            </p>
                            <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                                {emptyDescription || 'Try adjusting your search or filters'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <table className="w-full border-collapse">
                        <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--bg-card)' }}>
                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                {selectable && showCheckboxes && (
                                    <th className="w-12 px-3 py-3 text-center" style={{ width: '44px' }}>
                                        <button
                                            onClick={toggleSelectAll}
                                            className="inline-flex items-center justify-center w-5 h-5 rounded transition-colors duration-100"
                                            style={{
                                                border: '2px solid',
                                                borderColor: (filteredData?.length ?? 0) > 0 && filteredData?.every(r => selectedIds.has(r.id))
                                                    ? 'var(--accent)' : 'var(--border-default)',
                                                backgroundColor: (filteredData?.length ?? 0) > 0 && filteredData?.every(r => selectedIds.has(r.id))
                                                    ? 'var(--accent)'
                                                    : (filteredData?.length ?? 0) > 0 && filteredData?.some(r => selectedIds.has(r.id))
                                                        ? 'var(--accent)' : 'transparent',
                                                cursor: 'pointer',
                                                color: 'white',
                                            }}
                                            aria-label="Select all"
                                        >
                                            {(filteredData?.length ?? 0) > 0 && filteredData?.every(r => selectedIds.has(r.id))
                                                ? <Check size={12} strokeWidth={3} />
                                                : (filteredData?.length ?? 0) > 0 && filteredData?.some(r => selectedIds.has(r.id))
                                                    ? <Minus size={12} strokeWidth={3} />
                                                    : null}
                                        </button>
                                    </th>
                                )}
                                {allColumns.map(col => (
                                    <DataTableHeaderCell
                                        key={col.id}
                                        id={col.id}
                                        label={col.name}
                                        sortField={columnSortField(col)}
                                        sort={activeSort}
                                        width={colWidths[col.id] ?? col.width}
                                        onSort={handleSort}
                                        onResizeStart={(event, columnId) => {
                                            const header = event.currentTarget.parentElement as HTMLTableCellElement | null;
                                            if (header) thRefs.current.set(columnId, header);
                                            startResize(event, columnId);
                                        }}
                                        onResizeReset={resetColumnWidth}
                                    />
                                ))}
                            </tr>
                        </thead>
                        <tbody style={{ userSelect: selectable ? 'none' : undefined }}>
                            {displayedData.map((row, idx) => {
                                const isSelected = selectable && selectedIds.has(row.id);
                                return (
                                    <tr
                                        key={row.id || idx}
                                        className="transition-colors duration-100 group/row"
                                        style={{
                                            borderBottom: '1px solid var(--border-subtle)',
                                            backgroundColor: isSelected ? 'var(--accent-soft)' : 'transparent',
                                            cursor: selectable ? 'pointer' : 'default',
                                        }}
                                        onClick={(e) => {
                                            if (selectable) toggleSelection(row.id, idx, e.shiftKey, displayedData);
                                        }}
                                        onMouseOver={(e) => {
                                            if (!isSelected) {
                                                e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
                                            }
                                        }}
                                        onMouseOut={(e) => {
                                            e.currentTarget.style.backgroundColor = isSelected ? 'var(--accent-soft)' : 'transparent';
                                        }}
                                    >
                                        {selectable && showCheckboxes && (
                                            <td className="px-3 py-3 text-center" style={{ width: '44px' }}>
                                                <span
                                                    className="inline-flex items-center justify-center w-5 h-5 rounded transition-colors duration-100"
                                                    style={{
                                                        border: '2px solid',
                                                        borderColor: isSelected ? 'var(--accent)' : 'var(--border-default)',
                                                        backgroundColor: isSelected ? 'var(--accent)' : 'transparent',
                                                        color: 'white',
                                                    }}
                                                >
                                                    {isSelected && <Check size={12} strokeWidth={3} />}
                                                </span>
                                            </td>
                                        )}
                                        {allColumns.map(col => (
                                            <td key={col.id} className="px-4 py-3 text-sm" style={{ color: 'var(--text-primary)', width: colWidths[col.id] ?? col.width, maxWidth: colWidths[col.id] ?? col.width, wordBreak: 'break-word' }}>
                                                {col.render ? col.render(row) : (
                                                    <span>{getNestedValue(row, col.id)}</span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default DataTableNew;
